import type { ReactNode, RefObject } from "react";
import { X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  closeLabel: string;
  brand: ReactNode;
  children: ReactNode;
  returnFocusRef?: RefObject<HTMLButtonElement>;
}

export default function ConnectionManageSurface({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  brand,
  children,
  returnFocusRef,
}: Props) {
  const mobile = useIsMobile();
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onOpenChange(false);
    }
  };
  const restoreFocus = (event: Event) => {
    if (returnFocusRef?.current) {
      event.preventDefault();
      returnFocusRef.current.focus();
    }
  };

  if (mobile) {
    return (
      <Drawer autoFocus open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[88dvh] min-w-0 overflow-hidden" onCloseAutoFocus={restoreFocus}
          onEscapeKeyDown={handleEscapeKeyDown}>
          <DrawerHeader className="relative min-w-0 shrink-0 border-b border-border/70 pr-14 text-left">
            <div className="flex items-center gap-3">
              {brand}
              <div className="min-w-0">
                <DrawerTitle className="truncate text-xl">{title}</DrawerTitle>
                <DrawerDescription className="mt-1 line-clamp-2">{description}</DrawerDescription>
              </div>
            </div>
            <DrawerClose asChild>
              <button
                type="button"
                aria-label={closeLabel}
                className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-muted-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </DrawerClose>
          </DrawerHeader>
          <div className="min-h-0 min-w-0 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={closeLabel} className="flex max-h-[88dvh] max-w-xl flex-col overflow-hidden p-0" onCloseAutoFocus={restoreFocus} onEscapeKeyDown={handleEscapeKeyDown}>
        <DialogHeader className="min-w-0 shrink-0 border-b border-border/70 px-6 pb-5 pt-6 pr-14">
          <div className="flex items-center gap-3 text-left">
            {brand}
            <div className="min-w-0">
              <DialogTitle className="truncate text-xl">{title}</DialogTitle>
              <DialogDescription className="mt-1 line-clamp-2">{description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="min-h-0 min-w-0 overflow-y-auto overscroll-contain px-6 pb-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
